import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { defaultYear } from '../../common/constant';
import { request_imgfile2d_generation } from '../../common/receive'; // @todo
import { RootState } from '../../rootReducer';
import { AppThunk } from '../../store';
// @todo Consider if it is reasonable to import other slices.
import { selectDatabase, selectEnsemble, selectExperiment, selectFrequency, selectModel, selectSingleVariable } from '../fileRepertoire/fileRepertoireSlice';

interface Viewer2dOption {
  // Color bar.
  userDefinedColorBarEnabled: boolean;
  colorBarMin?: number;
  colorBarMax?: number;

  // Spatial domain.
  latMax: number;
  latMin: number;
  lonMax: number;
  lonMin: number;

  // Misc.
  imageSize: 'small' | 'medium' | 'large';
  time: string; // 'yyyy-MM-dd', 'yyyy-MM', or 'yyyy',
  unit: string;

  // Post.
  error?: string;
  image?: Blob;
  isLoading: boolean;
}

const initialState: Viewer2dOption = {
  // Color bar.
  userDefinedColorBarEnabled: false,

  // Spatial domain.
  latMax: 90,
  latMin: -90,
  lonMax: 180,
  lonMin: -180,

  // Misc.
  imageSize: 'medium',
  time: defaultYear.toString(),
  unit: 'original',

  // Post.
  isLoading: false,
};

const viewer2dOptionSlice = createSlice({
  name: 'viewer2dOption',
  initialState,
  reducers: {
    // Color bar.
    updateUserDefinedColorBarEnabled(state, action: PayloadAction<{userDefinedColorBarEnabled: Viewer2dOption['userDefinedColorBarEnabled']}>) {
      const { userDefinedColorBarEnabled } = action.payload;
      state.userDefinedColorBarEnabled = userDefinedColorBarEnabled;
    },
    updateColorBarMin(state, action: PayloadAction<{colorBarMin: Viewer2dOption['colorBarMin']}>) {
      const { colorBarMin } = action.payload;
      state.colorBarMin = colorBarMin;
    },
    updateColorBarMax(state, action: PayloadAction<{colorBarMax: Viewer2dOption['colorBarMax']}>) {
      const { colorBarMax } = action.payload;
      state.colorBarMax = colorBarMax;
    },

    // Spatial domain.
    updateLatMax(state, action: PayloadAction<{latMax: Viewer2dOption['latMax']}>) {
      const { latMax } = action.payload;
      state.latMax = latMax;
    },
    updateLatMin(state, action: PayloadAction<{latMin: Viewer2dOption['latMin']}>) {
      const { latMin } = action.payload;
      state.latMin = latMin;
    },
    updateLonMax(state, action: PayloadAction<{lonMax: Viewer2dOption['lonMax']}>) {
      const { lonMax } = action.payload;
      state.lonMax = lonMax;
    },
    updateLonMin(state, action: PayloadAction<{lonMin: Viewer2dOption['lonMin']}>) {
      const { lonMin } = action.payload;
      state.lonMin = lonMin;
    },

    // Misc.
    updateImageSize(state, action: PayloadAction<{imageSize: Viewer2dOption['imageSize']}>) {
      const { imageSize } = action.payload;
      state.imageSize = imageSize;
    },
    updateTime(state, action: PayloadAction<{time: Viewer2dOption['time']}>) {
      const { time } = action.payload;
      state.time = time;
    },
    updateUnit(state, action: PayloadAction<{unit: Viewer2dOption['unit']}>) {
      const { unit } = action.payload;
      state.unit = unit;
    },

    // Post.
    getImage2dStart(state) {
      state.error = undefined;
      state.isLoading = true;
    },
    getImage2dSuccess(state, action: PayloadAction<{image: Blob}>) {
      const { image } = action.payload;
      state.image = image;
      state.error = undefined;
      state.isLoading = false;
    },
    getImage2dFailure(state, action: PayloadAction<{message: string}>) {
      const { message } = action.payload;
      state.image = undefined;
      state.error = message;
      state.isLoading = false;
    },
  },
});

// Post.
export const fetchImage2d = (): AppThunk => async (dispatch, getState) => {
  const postData = selectPostData(getState());

  try {
    dispatch(getImage2dStart());

    const result = await request_imgfile2d_generation(postData);

    if (result == null) {
      throw new Error('Image file generation failed');
    }

    // Expects 'image/png' (successful) 'application/json' (unsuccessful)
    if (result.type.includes('json') || !result.type.includes('image') ) {
      const json = JSON.parse(await result.text());
      throw new Error(json.message);
    }

    dispatch(getImage2dSuccess({image: result}));
  } catch (error) {
    dispatch(getImage2dFailure({message: error.toString()}));
  }
};

export const {
  // Color bar.
  updateUserDefinedColorBarEnabled,
  updateColorBarMin,
  updateColorBarMax,

  // Spatial domain.
  updateLatMax,
  updateLatMin,
  updateLonMax,
  updateLonMin,

  // Misc.
  updateImageSize,
  updateTime,
  updateUnit,

  // Post.
  getImage2dStart,
  getImage2dSuccess,
  getImage2dFailure,
} = viewer2dOptionSlice.actions;

export default viewer2dOptionSlice.reducer;

// Color bar.
export const selectUserDefinedColorBarEnabled = (state: RootState) => {
  const { userDefinedColorBarEnabled } = state.viewer2dOption;
  return userDefinedColorBarEnabled;
};

export const selectColorBarMin = (state: RootState) => {
  const { colorBarMin } = state.viewer2dOption;
  return colorBarMin;
};

export const selectColorBarMax = (state: RootState) => {
  const { colorBarMax } = state.viewer2dOption;
  return colorBarMax;
};

// Spatial domain.
export const selectLatMax = (state: RootState) => {
  const { latMax } = state.viewer2dOption;
  return latMax;
};

export const selectLatMin = (state: RootState) => {
  const { latMin } = state.viewer2dOption;
  return latMin;
};

export const selectLonMax = (state: RootState) => {
  const { lonMax } = state.viewer2dOption;
  return lonMax;
};

export const selectLonMin = (state: RootState) => {
  const { lonMin } = state.viewer2dOption;
  return lonMin;
};

// Misc.
export const selectImageSize = (state: RootState) => {
  const { imageSize } = state.viewer2dOption;
  return imageSize;
};

export const selectTime = (state: RootState) => {
  const { time } = state.viewer2dOption;
  return time;
};

export const selectUnit = (state: RootState) => {
  const { unit } = state.viewer2dOption;
  return unit;
};

// Post.
export const selectError = (state: RootState) => {
  const { error } = state.viewer2dOption;
  return error;
};

export const selectImage2d = (state: RootState) => {
  const { image } = state.viewer2dOption;
  return image;
};

export const selectIsLoading = (state: RootState) => {
  const { isLoading } = state.viewer2dOption;
  return isLoading;
};

// PostData.
const _selectPostData = (state: RootState) => {
  const {
    userDefinedColorBarEnabled,
    colorBarMax,
    colorBarMin,

    latMax,
    latMin,
    lonMax,
    lonMin,

    imageSize,
    time,
    unit,
  } = state.viewer2dOption;

  const ret: {[prop: string]: unknown} = {
    useval: userDefinedColorBarEnabled,
    valmax: colorBarMax ?? null,
    valmin: colorBarMin ?? null,

    latmax: latMax,
    latmin: latMin,
    lonmax: lonMax,
    lonmin: lonMin,

    size: imageSize,
    time,
    unit,
  };

  return ret;
};

export const selectPostData = createSelector(
  _selectPostData,
  selectDatabase,
  selectEnsemble,
  selectExperiment,
  selectFrequency,
  selectModel,
  selectSingleVariable,
  (postData, database, ensemble, experiment, frequency, model, variable) => {
    return {
      ...postData,
      database,
      ensemble: ensemble ?? null,
      experiment: experiment ?? null,
      frequency,
      model,
      variable,
    };
  }
);
